/**
 * t522u: who may reply to and react on an announcement. Set in the composer's collapsed "Replies and reactions" row and
 * editable after posting. The default (everyone who can see it) is how every announcement worked before. The author,
 * owners and admins (anyone who can edit the post) can always reply and moderate. The API enforces this on every path
 * (comments, reactions on the post and on its comments, the assistant); clients only hide what the API would refuse.
 */
import type { AnnouncementPrincipal, InteractionPolicy } from './types';

export const REPLY_OPTIONS: { key: InteractionPolicy; label: string }[] = [
  { key: 'everyone', label: 'Everyone who can see it' }, { key: 'selected', label: 'Only selected people' }, { key: 'none', label: 'No one' },
];
export const REACT_OPTIONS: { key: InteractionPolicy; label: string }[] = [
  { key: 'everyone', label: 'Everyone' }, { key: 'selected', label: 'Only selected people' }, { key: 'none', label: 'No one' },
];
export const INTERACTIONS_LABEL = 'Replies and reactions';
export const INTERACTIONS_ALWAYS_NOTE = 'You, owners and admins can always reply and moderate.';
/** Shown under the comments when the caller can't reply - plain, not about them. */
export const REPLIES_OFF = 'Replies are turned off';
export const REPLIES_LIMITED = 'Only some people can reply to this announcement';

export const policyOf = (p: InteractionPolicy | null | undefined): InteractionPolicy => (p === 'selected' || p === 'none' ? p : 'everyone');

/** The muted one-line summary on the collapsed row. */
export function interactionsSummary(reply?: InteractionPolicy | null, react?: InteractionPolicy | null): string {
  const r = policyOf(reply), x = policyOf(react);
  if (r === 'everyone' && x === 'everyone') return 'Everyone can reply and react';
  const word = (p: InteractionPolicy) => (p === 'everyone' ? 'everyone' : p === 'selected' ? 'selected people' : 'no one');
  if (r === x) return `Replies and reactions: ${word(r)}`;
  return `Replies: ${word(r)} · Reactions: ${word(x)}`;
}

/** The line under an announcement's comments for someone who can't reply (null when they can). */
export function replyClosedLine(a: { reply_policy?: InteractionPolicy | null; can_reply?: boolean | null; can_edit?: boolean | null }): string | null {
  const none = policyOf(a.reply_policy) === 'none';
  if (a.can_reply !== false) return none && a.can_edit ? `${REPLIES_OFF} · you can still reply` : null;
  return none ? REPLIES_OFF : REPLIES_LIMITED;
}

/** Audience-picker value <-> principals (people, departments and groups, buildings, positions). */
export interface PrincipalPick { people: string[]; teams: string[]; buildings?: string[]; positions?: string[] }
export function principalsFromPick(v: PrincipalPick): AnnouncementPrincipal[] {
  return [
    ...v.people.map(id => ({ type: 'user' as const, id })), ...v.teams.map(id => ({ type: 'team' as const, id })),
    ...(v.buildings ?? []).map(id => ({ type: 'location' as const, id })), ...(v.positions ?? []).map(id => ({ type: 'position' as const, id })),
  ];
}
export function pickFromPrincipals(list: AnnouncementPrincipal[] | null | undefined): Required<PrincipalPick> {
  const of = (t: AnnouncementPrincipal['type']) => (list ?? []).filter(p => p.type === t && p.id).map(p => p.id!);
  return { people: of('user'), teams: of('team'), buildings: of('location'), positions: of('position') };
}
/** Ids only, deduplicated (what the API stores). */
export function cleanPrincipals(list: AnnouncementPrincipal[] | null | undefined): AnnouncementPrincipal[] {
  const seen = new Set<string>(); const out: AnnouncementPrincipal[] = [];
  for (const p of list ?? []) {
    const id = p.type === 'everyone' ? null : p.id ?? null;
    if (p.type !== 'everyone' && !id) continue;
    const k = `${p.type}:${id ?? ''}`;
    if (!seen.has(k)) { seen.add(k); out.push({ type: p.type, id }); }
  }
  return out;
}
